import { type NotificationApi, getLocalized } from "@klyra/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Bell, BellOff, RotateCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HttpError, notifications } from "../lib/api.ts";
import { isSubscribed, pushPermission, subscribePush } from "../lib/push.ts";
import { notifyError, notifySuccess } from "../lib/toast.ts";
import { DateText } from "./DateText.tsx";

const POLL_INTERVAL_MS = 30_000;

/** Cached shape of the notifications query — for optimistic cache edits. */
type NotifList = Awaited<ReturnType<typeof notifications.list>>;

const SEVERITY_DOT: Record<NotificationApi["severity"], string> = {
	info: "bg-blue-500",
	success: "bg-emerald-500",
	warning: "bg-amber-500",
	urgent: "bg-red-500",
};

export function NotificationBell() {
	const { t, i18n } = useTranslation("notifications");
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const lang = i18n.language.startsWith("ar") ? "ar" : "en";

	const query = useQuery({
		queryKey: ["notifications"],
		queryFn: () => notifications.list({ limit: 20 }),
		refetchInterval: POLL_INTERVAL_MS,
		refetchOnWindowFocus: true,
		staleTime: 0,
	});

	const markAllMutation = useMutation({
		mutationFn: () => notifications.markAllRead(),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["notifications"] });
			if (data.marked > 0) {
				notifySuccess(t("toast.marked_all", { count: data.marked }));
			}
		},
		onError: (err) => notifyError(err),
	});

	const markReadMutation = useMutation({
		mutationFn: (id: string) => notifications.markRead(id),
		// Optimistic: mark read + drop the unread count instantly.
		onMutate: async (id) => {
			await queryClient.cancelQueries({ queryKey: ["notifications"] });
			const prev = queryClient.getQueryData<NotifList>(["notifications"]);
			if (prev) {
				const wasUnread = prev.items.some((n) => n.id === id && !n.read_at);
				queryClient.setQueryData<NotifList>(["notifications"], {
					...prev,
					items: prev.items.map((n) =>
						n.id === id && !n.read_at
							? { ...n, read_at: new Date().toISOString() }
							: n,
					),
					unread_count: Math.max(0, prev.unread_count - (wasUnread ? 1 : 0)),
				});
			}
			return { prev };
		},
		onError: (_e, _id, ctx) => {
			if (ctx?.prev) queryClient.setQueryData(["notifications"], ctx.prev);
		},
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: ["notifications"] }),
	});

	const dismissMutation = useMutation({
		mutationFn: (id: string) => notifications.dismiss(id),
		// Optimistic: remove the row immediately; fix the unread count if needed.
		onMutate: async (id) => {
			await queryClient.cancelQueries({ queryKey: ["notifications"] });
			const prev = queryClient.getQueryData<NotifList>(["notifications"]);
			if (prev) {
				const wasUnread = prev.items.some((n) => n.id === id && !n.read_at);
				queryClient.setQueryData<NotifList>(["notifications"], {
					...prev,
					items: prev.items.filter((n) => n.id !== id),
					unread_count: Math.max(0, prev.unread_count - (wasUnread ? 1 : 0)),
				});
			}
			return { prev };
		},
		onError: (_e, _id, ctx) => {
			if (ctx?.prev) queryClient.setQueryData(["notifications"], ctx.prev);
		},
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: ["notifications"] }),
	});

	// Click-outside to close (desktop dropdown only — the mobile sheet has
	// its own scrim that handles taps outside).
	useEffect(() => {
		if (!open) return;
		const onClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", onClick);
		return () => document.removeEventListener("mousedown", onClick);
	}, [open]);

	// Lock body scroll while the mobile bottom-sheet is open so the page
	// underneath doesn't scroll when the user swipes inside the sheet.
	// ESC closes the sheet on desktop keyboards.
	useEffect(() => {
		if (!open) return;
		const isMobile = window.matchMedia("(max-width: 639px)").matches;
		if (isMobile) {
			const prev = document.body.style.overflow;
			document.body.style.overflow = "hidden";
			const onKey = (e: KeyboardEvent) => {
				if (e.key === "Escape") setOpen(false);
			};
			document.addEventListener("keydown", onKey);
			return () => {
				document.body.style.overflow = prev;
				document.removeEventListener("keydown", onKey);
			};
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open]);

	const items = query.data?.items ?? [];
	const unread = query.data?.unread_count ?? 0;

	const handleItemClick = (n: NotificationApi) => {
		if (!n.read_at) markReadMutation.mutate(n.id);
		if (n.link_path) {
			setOpen(false);
			navigate({ to: n.link_path });
			return;
		}
		// Fallback: many result-back notifications carry resource_type/id but no
		// explicit link_path. Resolve a route so the bell always navigates (works
		// for both the clinic doctor and the lab — /lab-order is tenant-scoped).
		if (n.resource_type === "lab_order" && n.resource_id) {
			setOpen(false);
			navigate({
				to: "/lab-order/$orderId",
				params: { orderId: n.resource_id },
			});
		}
	};

	// 401 means session is stale — silence here, the global guard handles it.
	const fatal =
		query.error instanceof HttpError && query.error.status !== 401
			? query.error
			: null;

	return (
		<div className="relative" ref={containerRef}>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="relative size-11 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted active:bg-border dark:active:bg-gray-700"
				aria-label={t("aria_label")}
				aria-expanded={open}
				aria-haspopup="dialog"
			>
				<BellIcon />
				{unread > 0 && (
					<span className="absolute top-1 end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-xs font-bold text-white flex items-center justify-center ring-2 ring-white dark:ring-gray-900">
						{unread > 99 ? "99+" : unread}
					</span>
				)}
			</button>

			{open && (
				<>
					{/* Mobile: full bottom-sheet. The sheet anchors to the bottom of
              the viewport, takes ~85% of the height, and respects the
              home-indicator safe area so dismiss-by-swipe still works.
              Desktop (>=sm): anchored dropdown beneath the bell. */}
					{/* Scrim — mobile only. Desktop relies on click-outside above. */}
					<button
						type="button"
						onClick={() => setOpen(false)}
						aria-label={t("aria_close", { defaultValue: "إغلاق" })}
						className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] animate-fade-in sm:hidden"
					/>

					{/* biome-ignore lint/a11y/useSemanticElements: custom bottom-sheet/dropdown panel with its own scrim + Esc handler; a native <dialog> swap would break the portal-free anchored positioning */}
					<div
						role="dialog"
						aria-modal="true"
						aria-label={t("title")}
						className={[
							// Shared
							"z-50 bg-card border-border overflow-hidden",
							// Mobile: bottom-sheet
							"fixed sm:absolute",
							"inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-auto sm:end-0 sm:start-auto",
							"sm:mt-2",
							"rounded-t-2xl sm:rounded-xl",
							"border-t sm:border",
							"shadow-2xl",
							"w-full sm:w-[360px]",
							"max-h-[85vh] sm:max-h-[min(70vh,560px)]",
							"flex flex-col",
							"animate-slide-up sm:animate-none",
						].join(" ")}
						style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
					>
						{/* Drag-handle visual cue (mobile only) — purely decorative.
                Adds the "you can swipe me away" affordance even though we
                actually close on scrim-tap, not swipe. */}
						<div className="flex justify-center pt-2 pb-1 sm:hidden">
							<span
								className="h-1.5 w-10 rounded-full bg-gray-300 dark:bg-gray-700"
								aria-hidden
							/>
						</div>

						<div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
							<h3 className="font-semibold text-base sm:text-sm">
								{t("title")}
							</h3>
							<div className="flex items-center gap-1">
								{unread > 0 && (
									<button
										type="button"
										onClick={() => markAllMutation.mutate()}
										className="text-sm sm:text-xs text-brand-600 hover:underline px-2 py-1"
										disabled={markAllMutation.isPending}
									>
										{t("mark_all_read")}
									</button>
								)}
								<button
									type="button"
									onClick={() => setOpen(false)}
									aria-label={t("aria_close", { defaultValue: "إغلاق" })}
									className="size-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted sm:hidden text-xl leading-none"
								>
									×
								</button>
							</div>
						</div>

						<PushToggle />

						<div
							ref={scrollRef}
							className="flex-1 overflow-y-auto overscroll-contain"
						>
							{query.isLoading && (
								<p className="text-xs text-muted-foreground text-center py-6">
									{t("loading")}
								</p>
							)}
							{fatal && (
								<p className="text-xs text-red-600 text-center py-6">
									{t("load_error")}
								</p>
							)}
							{!query.isLoading && items.length === 0 && (
								<div className="text-center py-12 sm:py-8">
									<BellOff
										className="size-9 sm:size-8 mx-auto mb-3 sm:mb-2 text-muted-foreground"
										strokeWidth={1.5}
										aria-hidden
									/>
									<p className="text-sm sm:text-xs text-muted-foreground">
										{t("empty")}
									</p>
								</div>
							)}
							{items.map((n) => (
								<NotificationRow
									key={n.id}
									n={n}
									lang={lang}
									scrollRoot={scrollRef}
									onClick={() => handleItemClick(n)}
									onSeen={() => markReadMutation.mutate(n.id)}
									onDismiss={() => dismissMutation.mutate(n.id)}
								/>
							))}
						</div>
					</div>
				</>
			)}
		</div>
	);
}

/** Dwell time before an on-screen notification is auto-marked as seen. */
const AUTO_READ_DWELL_MS = 1200;

function NotificationRow({
	n,
	lang,
	scrollRoot,
	onClick,
	onSeen,
	onDismiss,
}: {
	n: NotificationApi;
	lang: "ar" | "en";
	scrollRoot: React.RefObject<HTMLDivElement | null>;
	onClick: () => void;
	onSeen: () => void;
	onDismiss: () => void;
}) {
	const { t } = useTranslation("notifications");
	const unread = !n.read_at;
	const rowRef = useRef<HTMLDivElement>(null);

	// Keep the latest onSeen without re-creating the observer every render.
	const onSeenRef = useRef(onSeen);
	onSeenRef.current = onSeen;

	// Smart auto-read: when an unread row stays visible in the panel for a
	// short dwell, mark it seen so the blue dot disappears the moment the
	// user actually looks at it (not just on click). Read rows skip this.
	useEffect(() => {
		if (!unread) return;
		const el = rowRef.current;
		if (!el || typeof IntersectionObserver === "undefined") return;

		let timer: ReturnType<typeof setTimeout> | null = null;
		const clear = () => {
			if (timer) {
				clearTimeout(timer);
				timer = null;
			}
		};
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
						if (!timer)
							timer = setTimeout(() => onSeenRef.current(), AUTO_READ_DWELL_MS);
					} else {
						clear();
					}
				}
			},
			{ root: scrollRoot.current ?? null, threshold: [0, 0.7, 1] },
		);
		observer.observe(el);
		return () => {
			clear();
			observer.disconnect();
		};
	}, [unread, scrollRoot]);

	return (
		<div
			ref={rowRef}
			className={`group flex gap-3 px-4 py-3.5 sm:py-3 border-b border-border last:border-b-0 cursor-pointer transition-colors min-h-[64px] sm:min-h-0 ${
				unread
					? "bg-brand-50/40 dark:bg-brand-950/20 hover:bg-brand-50 dark:hover:bg-brand-950/40"
					: "hover:bg-muted"
			}`}
			onClick={onClick}
			// biome-ignore lint/a11y/useSemanticElements: clickable row wraps nested interactive content (external link + dismiss button), so a native <button> would nest interactive elements (invalid HTML); keyboard support is provided via onKeyDown below
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onClick();
				}
			}}
		>
			{/* Unread indicator. The dot is present only while unread (severity
          colour — blue for info, red for urgent…) and vanishes the moment
          the notification is read/seen. An invisible same-size placeholder
          keeps the text aligned across read and unread rows. */}
			<span
				className={`mt-2 size-2.5 shrink-0 rounded-full transition-opacity ${
					unread ? SEVERITY_DOT[n.severity] : "opacity-0"
				}`}
				aria-label={unread ? t("unread") : undefined}
			/>
			<div className="flex-1 min-w-0">
				<p className="text-sm font-medium leading-snug">
					{getLocalized(n.title, lang)}
				</p>
				{n.body && (
					<p className="text-xs sm:text-[13px] text-muted-foreground mt-1 leading-relaxed line-clamp-2">
						{getLocalized(n.body as { ar: string; en?: string }, lang)}
					</p>
				)}
				<div className="flex items-center gap-2 mt-1.5 flex-wrap">
					<span className="text-xs text-muted-foreground">
						<DateText value={n.created_at} withTime />
					</span>
					{n.external_url && (
						<a
							href={n.external_url}
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => e.stopPropagation()}
							className="text-xs text-emerald-600 hover:underline font-medium"
						>
							{t("open_whatsapp")} →
						</a>
					)}
					{n.type === "appointment_cancelled" &&
						typeof n.metadata?.patient_id === "string" && (
							<RescheduleLink patientId={n.metadata.patient_id} />
						)}
				</div>
			</div>
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onDismiss();
				}}
				// Mobile: always-visible 44×44 tap target. Desktop: hover-reveal.
				className="self-start text-muted-foreground hover:text-foreground sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-2xl leading-none size-11 sm:size-7 flex items-center justify-center -mt-1 sm:mt-0 shrink-0"
				aria-label={t("dismiss")}
			>
				×
			</button>
		</div>
	);
}

function RescheduleLink({ patientId }: { patientId: string }) {
	const { t } = useTranslation("notifications");
	const navigate = useNavigate();
	return (
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				navigate({
					to: "/appointments/new",
					search: { patient_id: patientId } as Record<string, string>,
				});
			}}
			className="text-xs text-brand-600 hover:underline font-medium inline-flex items-center gap-1"
		>
			<RotateCw className="size-3" strokeWidth={2} aria-hidden />{" "}
			{t("reschedule")}
		</button>
	);
}

function BellIcon() {
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			focusable={false}
		>
			<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
			<path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
		</svg>
	);
}

/**
 * In-panel prompt to turn on device (web push) notifications. Hidden when
 * push is unsupported or already enabled; shows a guidance line if the user
 * previously blocked notifications.
 */
function PushToggle() {
	const { t } = useTranslation("common");
	const [perm, setPerm] = useState<NotificationPermission | "unsupported">(() =>
		pushPermission(),
	);
	const [subscribed, setSubscribed] = useState(false);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		isSubscribed()
			.then(setSubscribed)
			.catch(() => setSubscribed(false));
	}, []);

	if (perm === "unsupported") return null;
	if (perm === "granted" && subscribed) return null;

	if (perm === "denied") {
		return (
			<p className="px-4 py-2 text-xs text-amber-600 dark:text-amber-400 border-b border-border shrink-0">
				{t("push.blocked")}
			</p>
		);
	}

	const onEnable = async () => {
		setBusy(true);
		try {
			const ok = await subscribePush();
			setSubscribed(ok);
		} catch {
			/* ignore — button stays for retry */
		} finally {
			setBusy(false);
			setPerm(pushPermission());
		}
	};

	return (
		<button
			type="button"
			onClick={onEnable}
			disabled={busy}
			className="w-full shrink-0 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-950/30 border-b border-border hover:bg-brand-100 dark:hover:bg-brand-950/50 disabled:opacity-60"
		>
			<Bell className="size-4" strokeWidth={2} aria-hidden />
			{busy ? "…" : t("push.enable")}
		</button>
	);
}
